import {useFlux, useFluxListener} from '@nlabs/arkhamjs-utils-react';
import {Button} from '@nlabs/gothamjs/components';
import {AlertCircle, CheckCircle, RefreshCw, X} from '@nlabs/gothamjs/icons';
import {Link, useLocation, useNavigate} from '@nlabs/gothamjs/router';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {createWorkspaceActions, WORKSPACE_EVENTS} from '../../state/workspaceActions.js';
import {getStoryProject, getStoryProjects} from '../../state/workspaceSelectors.js';
import {iconButton, primaryButton} from '../../theme/classes.js';
import {AppShell} from '../AppShell/AppShell.js';
import {Assets} from '../Assets/Assets.js';
import {Billing} from '../Billing/Billing.js';
import {Characters} from '../Characters/Characters.js';
import {OneOffVideo} from '../OneOffVideo/OneOffVideo.js';
import {ProjectEditor} from '../ProjectEditor/ProjectEditor.js';
import {ProjectList} from '../ProjectList/ProjectList.js';
import {Studio} from '../Studio/Studio.js';

import type {Workspace} from '@reactorbox/shared';
import type {ReactElement} from 'react';
import type {Selection, View} from '../../state/workspaceActions.js';

export const App = ({signedIn = false}: {readonly signedIn?: boolean}): ReactElement => {
  const flux = useFlux();
  const location = useLocation();
  const navigateRoute = useNavigate();
  const actions = useMemo(() => createWorkspaceActions(flux, (next) => {
    const search = new URLSearchParams();
    if(next.projectId) {
      search.set('project', next.projectId);
    }
    void navigateRoute({pathname: `/${next.view}`, search: search.toString()}, {state: {createProject: Boolean(next.createProject)}});
  }), [flux, navigateRoute]);
  const [workspace, setWorkspace] = useState<Workspace>();
  const selection: Selection = {projectId: new URLSearchParams(location.search).get('project') || undefined, view: location.pathname.slice(1) as View};
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState<'new' | 'existing'>();
  useFluxListener(WORKSPACE_EVENTS.PENDING, useCallback(() => {
    setBusy(true);
    setError('');
  }, []));
  useFluxListener(WORKSPACE_EVENTS.CHANGED, useCallback((event?: {notice?: string}) => {
    setWorkspace(flux.getState('workspace.data') as Workspace);
    setBusy(false);
    if(event?.notice) {
      setNotice(event.notice);
      setEditing(undefined);
      if(location.state?.createProject) {
        void navigateRoute({pathname: location.pathname, search: location.search}, {replace: true, state: null});
      }
    }
  }, [flux, location.pathname, location.search, location.state, navigateRoute]));
  useFluxListener(WORKSPACE_EVENTS.ERROR, useCallback(() => {
    setError(flux.getState('workspace.error', '') as string);
    setBusy(false);
  }, [flux]));
  useEffect(() => {
    void actions.load();
  }, [actions]);
  useEffect(() => {
    if(!notice) {
      return undefined;
    }
    const timer = window.setTimeout(() => setNotice(''), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);
  useEffect(() => {
    setEditing(location.state?.createProject ? 'new' : undefined);
    setNotice('');
  }, [location.key, location.state]);
  const hasPending = workspace?.jobs.some((job) => ['queued', 'submitting', 'processing'].includes(job.status)) || workspace?.creativeJobs?.some((job) => ['queued', 'submitting'].includes(job.status));
  useEffect(() => {
    if(!hasPending) {
      return undefined;
    }
    const timer = window.setInterval(() => void actions.load(), 1500);
    return () => window.clearInterval(timer);
  }, [actions, hasPending]);
  const storyProjects = getStoryProjects(workspace);
  const project = getStoryProject(workspace, selection.projectId);
  const navigate = (view: View): void => {
    void actions.select({projectId: project?.id, view});
  };
  const open = (projectId: string): void => {
    void actions.select({projectId, view: 'studio'});
  };
  let content: ReactElement;
  if(!workspace) {
    content = <div className="flex min-h-[70vh] flex-col items-center justify-center gap-5 text-center [&_h1]:text-3xl [&_p]:max-w-sm [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-white/50"><span><RefreshCw className={busy ? 'motion-safe:animate-spin' : ''} size={27} /></span><h1>{error ? 'Let’s reconnect your studio.' : 'Setting the scene…'}</h1><p>{error ? 'Your workspace is unavailable. Make sure the API is running and try again.' : 'Loading your stories, characters, and creative workspace.'}</p>{!busy && <Button className={primaryButton} onClick={() => void actions.load()}>Try again</Button>}</div>;
  } else if(editing) {
    content = <ProjectEditor busy={busy} key={editing} onCancel={() => setEditing(undefined)} onSave={(body) => void actions.mutate(editing === 'existing' && project ? `/api/projects/${project.id}` : '/api/projects', body, editing === 'existing' ? 'Project updated.' : 'Project created. Open it to start your storyboard.', editing === 'existing' ? 'PATCH' : 'POST')} project={editing === 'existing' ? project : undefined} />;
  } else if(workspace.plan === 'free' && ['videos', 'assets'].includes(selection.view)) {
    content = <section className="mx-auto max-w-2xl py-20 text-center"><h1 className="text-4xl text-violet-200">Plan your story for free.</h1><p className="mt-6 leading-7 text-white/60">Create projects, add characters, and write your shots. Image generation, video generation, and video storage require a paid plan.</p><Link className={`${primaryButton} mt-8 inline-flex`} to="/projects">Build your story</Link><Link className="ml-6 text-sky-200" to="/pricing">Explore plans</Link></section>;
  } else if(selection.view === 'videos') {
    content = <OneOffVideo actions={actions} busy={busy} onCreateProject={() => void actions.select({createProject: true, view: 'projects'})} workspace={workspace} />;
  } else if(selection.view === 'studio' && project) {
    content = <Studio actions={actions} busy={busy} key={project.id} onBack={() => navigate('projects')} onCharacters={() => navigate('characters')} onEdit={() => setEditing('existing')} project={project} workspace={workspace} />;
  } else if(selection.view === 'characters' && project) {
    content = <Characters actions={actions} busy={busy} key={`${project.id}-${project.characters.length}`} project={project} />;
  } else if(selection.view === 'assets') {
    content = <Assets workspace={workspace} />;
  } else if(selection.view === 'billing') {
    content = <Billing signedIn={signedIn} workspace={workspace} />;
  } else {
    content = <ProjectList onCreate={() => setEditing('new')} onOpen={open} onVideo={() => navigate('videos')} workspace={workspace} />;
  }
  return <AppShell balance={workspace?.credits.available || 0} mode={workspace?.mode || 'demo'} onNavigate={navigate} projectCount={storyProjects.length} signedIn={signedIn} view={selection.view}>{error && <div className="mb-6 flex items-start gap-4 rounded-3xl border border-rose-300/25 bg-rose-300/10 p-5 text-sm text-rose-100 [&_p]:mt-1 [&_p]:break-words [&_p]:text-rose-100/70" role="alert"><AlertCircle size={18} /><div><strong>We couldn’t complete that request.</strong><p>{error}</p></div><Button aria-label="Dismiss error" className={iconButton} onClick={() => setError('')} variant="text"><X size={16} /></Button></div>}{content}{notice && <div className="fixed right-4 bottom-6 left-4 z-50 mx-auto flex w-fit max-w-[calc(100%-2rem)] items-center gap-3 rounded-full border border-white/15 bg-neutral-900/90 px-5 py-4 text-sm text-white shadow-2xl backdrop-blur-2xl [&_button]:ml-2" role="status"><CheckCircle size={18} /><span>{notice}</span><button aria-label="Dismiss notification" onClick={() => setNotice('')} type="button"><X size={14} /></button></div>}</AppShell>;
};
